import {
  createContext, FC, useCallback, useContext, useRef, useEffect, useState,
} from 'react';
import { stringify } from 'querystring';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';
import startOfMonth from 'date-fns/startOfMonth';
import startOfToday from 'date-fns/startOfToday';
import { BOOKING_MOTIVATOR_STATES } from 'src/common/constants';
import { noop } from 'src/common/helpers';
import { differenceInDays } from 'date-fns';
import { BookingMotivators, Motivator } from '../types/api/v4/bookingMotivators';
import { apiRequest } from '../utils/apiFetch';
import captureException from '../utils/captureException';
import { DateString, formatDate } from '../utils/dateHelpers';

type UseBookingMotivators = {
  bookingMotivators: BookingMotivatorsState;
  getSelectedMotivators: (
    dates: Date[],
    ignoreNoSameDayPricing?: boolean
  ) => Motivator[];
  getMultiSelectedMotivators: (dates: Date[]) => Motivator[];
  isDateUnavailable: (dates: Date[], isMultiDay: boolean) => boolean;
  changeVisibleCalendarMonth: (date: Date) => void;
  setInstabookDates: (value: string[]) => void;
  loadBookingMotivators: (date: string, ignoreCache?: boolean) => void;
};

type Period = 'day' | 'week' | 'month';

type BookingMotivatorsProviderProps = {
  boatId: string;
  // Initial booking motivators state
  bookingMotivators?: BookingMotivatorsState;
  initialInstabookDates?: string[];
  children: React.ReactNode;
};

export type BookingMotivatorsState = Record<DateString, Motivator[]>;

export type BlockedTime = {
  start_time: string;
  end_time: string;
};

export const CALENDAR_DATE_FORMAT = 'yyyy-MM-dd';
const INSTABOOK_EXPIRING_DAYS = 4;

// Priority order for non-Instabook motivators
const motivatorPriority = [
  BOOKING_MOTIVATOR_STATES.LIMITED_AVAILABILITY,
  BOOKING_MOTIVATOR_STATES.HIGH_DEMAND,
  BOOKING_MOTIVATOR_STATES.IN_DEMAND,
  BOOKING_MOTIVATOR_STATES.UNUSUAL_SIGHTING,
];
const MAX_SELECTED_MOTIVATORS = 2;

const isBookingMotivator = (motivator: any): motivator is Motivator => (
  Boolean(BOOKING_MOTIVATOR_STATES[motivator])
);

const safeFormatDate = (date: Date | null): string => (
  date
    ? formatDate(date)
    : ''
);

const getBookingMotivatorsUrl = (boatId: string, startDate: string, period: Period = 'month') => (
  `/api/v4/boats/${boatId}/booking-motivators/?${stringify({
    period,
    start_date: startDate,
  })}`
);

const getBookingMotivators = async ({ boatId, startDate, period }) => {
  try {
    const url = getBookingMotivatorsUrl(boatId, startDate, period);
    const response = await apiRequest(url);
    if (response.status === 200) {
      const data: BookingMotivators[] = await response.json();
      return data;
    }
    return [];
  } catch (error: unknown) {
    captureException(error);
    return [];
  }
};

const startMonth = startOfMonth(startOfToday());

const blockingList = [
  BOOKING_MOTIVATOR_STATES.UNAVAILABLE,
  BOOKING_MOTIVATOR_STATES.OFF_SEASON,
  BOOKING_MOTIVATOR_STATES.LIMITED_AVAILABILITY,
];

const inDemandList = [
  BOOKING_MOTIVATOR_STATES.HIGH_DEMAND,
  BOOKING_MOTIVATOR_STATES.IN_DEMAND,
];

const addOnsList = [
  BOOKING_MOTIVATOR_STATES.UNUSUAL_SIGHTING,
];

/**
 * Handles motivators, instabook dates
 */
const useBookingMotivators = (
  boatId: string,
  initialBookingMotivators: BookingMotivatorsState,
  initialInstabookDates: string[] = [],
): UseBookingMotivators => {
  const [instabookDates, setInstabookDates] = useState<string[]>(initialInstabookDates);
  const [calendarMonthStart, setCalendarMonthStart] = useState<Date>(() => (
    startOfMonth(new Date())
  ));
  const loadedMonths = useRef(new Set([0, 1, 2]
    .map((offset) => safeFormatDate(addMonths(startMonth, offset)))));
  const [
    bookingMotivators,
    setBookingMotivators,
  ] = useState<BookingMotivatorsState>(initialBookingMotivators);

  const changeVisibleCalendarMonth = useCallback((date: Date) => {
    const month = startOfMonth(date);
    setCalendarMonthStart(month);
  }, []);

  const getSelectedMotivators = useCallback((
    dates: Date[],
    ignoreNoSameDayPricing: boolean = false,
  ): Motivator[] => {
    const selectedDate = dates?.[0];
    if (!selectedDate) {
      return [];
    }
    const receivedMotivators = bookingMotivators[safeFormatDate(selectedDate)] ?? [];
    let selectedMotivators: Motivator[] = [];

    /**
     * We only display 2 motivators at a time,
     * unless either of these are returned: they are displayed alone:
     * 1. Unavailable - handled because backend will return this on its own
     * 2. Looks Good
     *
     * In other cases, these are added to the list, in this order:
     * 1. Instabook: Expiring
     * 2. Limited Availability or Availability Pending
     * 3. Instabook
     * 4. High Demand
     * 5. In Demand
     * 6. Unusual Sighting
     *
     * For New Pricing v2, there are 2 more motivators are introduced:
     * 1. Off season.
     * 2. Weekday Off
     */

    const isUnavailable = receivedMotivators.includes(BOOKING_MOTIVATOR_STATES.UNAVAILABLE);
    const isOffSeason = receivedMotivators.includes(BOOKING_MOTIVATOR_STATES.OFF_SEASON);
    const isWeekdayOff = !ignoreNoSameDayPricing
    && receivedMotivators.includes(BOOKING_MOTIVATOR_STATES.NO_SAME_DAY_PRICING);

    // Early return of Unavailable motivator
    if (isUnavailable) {
      selectedMotivators = [BOOKING_MOTIVATOR_STATES.UNAVAILABLE];
    } else if (isOffSeason) {
      selectedMotivators = [BOOKING_MOTIVATOR_STATES.OFF_SEASON];
    } else if (isWeekdayOff) {
      selectedMotivators = [BOOKING_MOTIVATOR_STATES.NO_SAME_DAY_PRICING];
    } else {
      const hasInstabookTrips = instabookDates.includes(safeFormatDate(selectedDate));
      const daysUntilInstabook = differenceInDays(selectedDate, startOfToday());
      const showInstabookExpiring = hasInstabookTrips
        && daysUntilInstabook <= INSTABOOK_EXPIRING_DAYS;

      // If there are Instabook trips, add either Instabook Expiring or Instabook
      if (hasInstabookTrips && !selectedMotivators.includes(BOOKING_MOTIVATOR_STATES.UNAVAILABLE)) {
        if (selectedMotivators.length === 0) {
          selectedMotivators = [
            showInstabookExpiring
              ? BOOKING_MOTIVATOR_STATES.INSTABOOK_EXPIRING
              : BOOKING_MOTIVATOR_STATES.INSTABOOK,
          ];
        } else {
          selectedMotivators.push(
            showInstabookExpiring
              ? BOOKING_MOTIVATOR_STATES.INSTABOOK_EXPIRING
              : BOOKING_MOTIVATOR_STATES.INSTABOOK,
          );
        }
      }

      // Add motivators in priority order until we reach the limit of 2
      for (const motivator of motivatorPriority) {
        const canAddMore = selectedMotivators.length < MAX_SELECTED_MOTIVATORS;
        const isAvailable = receivedMotivators.includes(motivator);
        const notAlreadySelected = !selectedMotivators.includes(motivator);

        if (canAddMore && isAvailable && notAlreadySelected) {
          selectedMotivators.push(motivator);
          if (selectedMotivators.length === MAX_SELECTED_MOTIVATORS) break;
        }
      }

      // Default to "Looks Good" if no motivators were selected
      if (selectedMotivators.length === 0) {
        selectedMotivators = [BOOKING_MOTIVATOR_STATES.LOOKS_GOOD];
      }
    }

    return selectedMotivators;
  }, [bookingMotivators, instabookDates]);

  const getMultiSelectedMotivators = useCallback((
    dates: Date[],
  ): Motivator[] => {
    const [start, end] = dates;
    const selectedMotivators: Motivator[] = [];
    if (start && end) {
      const formattedStart = safeFormatDate(start);
      const formattedEnd = safeFormatDate(end);
      const keys = Object.keys(bookingMotivators);
      let foundInDemandMotivator = false;
      let foundAddOnsMotivator = false;

      keys.every((key) => {
        if (key >= formattedStart && key <= formattedEnd) {
          const bookingMotivator = bookingMotivators[key];
          // Handle blocking motivators first
          // Only return first blocking motivator from list
          const blockingMotivator = blockingList.find((m) => bookingMotivator.includes(m));

          if (blockingMotivator) {
            // If blocking, we need to reset any accrued motivators to the blocking one
            selectedMotivators.push(blockingMotivator);
            return false;
          }

          // Check if any motivators are add-ons
          // Motivator can be stacked with non-blocking motivators
          if (!foundAddOnsMotivator) {
            const addOnsMotivators = addOnsList.find((m) => bookingMotivator.includes(m));
            if (addOnsMotivators) {
              selectedMotivators.push(addOnsMotivators);
              // only show one
              foundAddOnsMotivator = true;
            }
          }

          if (!foundInDemandMotivator) {
            // Only show the first in-demand motivator; we don't want these to stack
            const inDemandMotivators = inDemandList.find((m) => bookingMotivator.includes(m));
            if (inDemandMotivators) {
              selectedMotivators.push(inDemandMotivators);
              foundInDemandMotivator = true;
            }
          }
        }

        return true; // Continue to check next day
      });
      // Apply Looks Good as default
      if (!selectedMotivators.length) {
        selectedMotivators.push(BOOKING_MOTIVATOR_STATES.LOOKS_GOOD);
      }
    }

    return start && !end ? getSelectedMotivators([start], true) : selectedMotivators;
  }, [bookingMotivators, getSelectedMotivators]);

  const isDateUnavailable = (dates: Date[], isMultiDay: boolean): boolean => {
    if (isMultiDay) {
      const motivators = getMultiSelectedMotivators(dates);
      if (motivators.includes(BOOKING_MOTIVATOR_STATES.UNAVAILABLE)
        || motivators.includes(BOOKING_MOTIVATOR_STATES.OFF_SEASON)
      ) {
        return true;
      }
    }
    if (!isMultiDay) {
      const motivators = getSelectedMotivators([dates[0]]);
      if (motivators.includes(BOOKING_MOTIVATOR_STATES.UNAVAILABLE)
        || motivators.includes(BOOKING_MOTIVATOR_STATES.OFF_SEASON)
        || motivators.includes(BOOKING_MOTIVATOR_STATES.NO_SAME_DAY_PRICING)) {
        return true;
      }
    }
    return false;
  };

  const mergeBookingMotivators = useCallback((
    state: BookingMotivatorsState,
    newBookingMotivators: BookingMotivators[],
  ) => ({
    ...state,
    ...newBookingMotivators.reduce((
      bookingMotivatorsAcc: BookingMotivatorsState,
      bookingMotivator: BookingMotivators,
    ) => {
      // eslint-disable-next-line no-param-reassign
      bookingMotivatorsAcc[bookingMotivator.date] = (
        bookingMotivator.motivators.filter(isBookingMotivator)
      );
      return bookingMotivatorsAcc;
    }, {}),
  }), []);

  const loadBookingMotivators = useCallback((date, ignoreCache = false) => {
    if (!ignoreCache && (loadedMonths.current.has(date) || !boatId)) {
      return;
    }
    loadedMonths.current.add(date);
    getBookingMotivators({ boatId, startDate: date, period: 'month' })
      .then((monthData) => {
        setBookingMotivators((currentState) => mergeBookingMotivators(currentState, monthData));
      });
  }, [boatId, mergeBookingMotivators]);

  // Preload the next month's data when the active month changes so it is
  // available when the user navigates to the next month.
  useEffect(() => {
    const currentMonth = safeFormatDate(calendarMonthStart);
    const previousMonth = safeFormatDate(subMonths(calendarMonthStart, 1));
    const nextMonth = safeFormatDate(addMonths(calendarMonthStart, 1));
    const todayMonth = safeFormatDate(startMonth);

    // Load previous, current, and next month if not already loaded
    // Only load previous month if it's not before today's month
    // TODO: single request
    if (!loadedMonths.current.has(previousMonth) && previousMonth >= todayMonth) {
      loadBookingMotivators(previousMonth);
    }
    if (!loadedMonths.current.has(currentMonth)) {
      loadBookingMotivators(currentMonth);
    }
    if (!loadedMonths.current.has(nextMonth)) {
      loadBookingMotivators(nextMonth);
    }
  }, [loadBookingMotivators, calendarMonthStart]);

  return {
    bookingMotivators,
    getSelectedMotivators,
    getMultiSelectedMotivators,
    isDateUnavailable,
    changeVisibleCalendarMonth,
    setInstabookDates,
    loadBookingMotivators,
  };
};

const initialContext: UseBookingMotivators = {
  bookingMotivators: {},
  getSelectedMotivators: () => [],
  getMultiSelectedMotivators: () => [],
  isDateUnavailable: () => false,
  changeVisibleCalendarMonth: noop,
  setInstabookDates: noop,
  loadBookingMotivators: noop,
};

const BookingMotivatorContext = createContext<UseBookingMotivators>(initialContext);

const useBookingMotivatorsContext = (): UseBookingMotivators => useContext(BookingMotivatorContext);

export const BookingMotivatorsProvider: FC<BookingMotivatorsProviderProps> = ({
  boatId,
  bookingMotivators: initialBookingMotivators = {},
  initialInstabookDates = [],
  children,
}) => {
  const bookingMotivators = useBookingMotivators(
    boatId,
    initialBookingMotivators,
    initialInstabookDates,
  );
  return (
    <BookingMotivatorContext.Provider value={bookingMotivators}>
      {children}
    </BookingMotivatorContext.Provider>
  );
};

export default useBookingMotivatorsContext;
